import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import { ArrowUp, ArrowUpRight, MapPin } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT } from "@/content";
import { trackEvent } from "@/analytics";
import { Overline, SerifHeading, FadeUp, EASE } from "@/primitives";

/* --------------------------- How it works ------------------------------ */

export const HowItWorks = ({ t }) => (
  <section id="how" data-testid="how-section" className="py-24 md:py-36 border-t border-clay/60">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-4">
          <FadeUp>
            <Overline>— {t.howItWorks.overline}</Overline>
          </FadeUp>
          <FadeUp delay={0.1}>
            <SerifHeading className="mt-6 text-[44px] md:text-[64px] whitespace-pre-line">
              {t.howItWorks.title}
            </SerifHeading>
          </FadeUp>
        </div>
        <ol className="md:col-span-8 grid md:grid-cols-3 gap-px bg-clay/60 border border-clay/60">
          {t.howItWorks.steps.map((s, i) => (
            <motion.li
              key={s.n}
              data-testid={`how-step-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
              className="group relative bg-sand p-8 md:p-10 min-h-[300px] flex flex-col gap-8 hover:bg-sand-deep transition-colors duration-700"
            >
              <span className="font-serif-display italic text-[64px] md:text-[80px] leading-none text-clay group-hover:text-matcha transition-colors duration-700">
                {s.n}
              </span>
              <div>
                <h3 className="font-serif-display text-2xl md:text-[28px] text-ink leading-tight">
                  {s.title}
                </h3>
                <p className="mt-4 text-ink-soft text-[14px] leading-relaxed">{s.p}</p>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </div>
  </section>
);

/* -------------------------------- FAQ ---------------------------------- */

export const FAQ = ({ t }) => (
  <section id="faq" data-testid="faq-section" className="py-24 md:py-36">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16">
      <div className="md:col-span-4">
        <FadeUp>
          <Overline>— {t.faq.overline}</Overline>
        </FadeUp>
        <FadeUp delay={0.1}>
          <SerifHeading className="mt-6 text-[44px] md:text-[64px]">{t.faq.title}</SerifHeading>
        </FadeUp>
        <FadeUp delay={0.2}>
          <a
            href={CONTACT.whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faq-whatsapp"
            onClick={() => trackEvent("whatsapp_click", { source: "faq" })}
            className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-ink border-b border-ink/40 pb-1 hover:text-matcha hover:border-matcha transition-colors"
          >
            <FaWhatsapp className="w-4 h-4" />
            {t.contact.whatsapp}
          </a>
        </FadeUp>
      </div>
      <FadeUp delay={0.15} className="md:col-span-8">
        <Accordion type="single" collapsible className="border-t border-clay/70">
          {t.faq.items.map((item, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="border-b border-clay/70"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                onClick={() => trackEvent("faq_open", { index: i })}
                className="py-6 md:py-7 text-left font-serif-display text-[22px] md:text-[26px] font-light text-ink hover:no-underline hover:text-matcha transition-colors [&>svg]:w-5 [&>svg]:h-5 [&>svg]:text-matcha"
              >
                <span className="flex items-baseline gap-5">
                  <span className="text-[11px] font-sans-body tracking-[0.22em] text-matcha/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {item.q}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-7 pl-10 text-ink-soft text-[15px] leading-relaxed max-w-2xl">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </FadeUp>
    </div>
  </section>
);

/* ------------------------------ Map embed ------------------------------ */

export const MapEmbed = ({ t }) => (
  <FadeUp delay={0.25} className="mt-14 md:mt-20">
    <div
      data-testid="map-embed"
      className="relative border border-sand/30 bg-matcha-deep/40 overflow-hidden"
    >
      <iframe
        title="Nunu Massage location map"
        src={CONTACT.mapEmbedUrl}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
        className="w-full h-[320px] md:h-[420px] grayscale-[20%] saturate-[0.85]"
      />
      <a
        href={CONTACT.mapUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="map-embed-open"
        onClick={() => trackEvent("map_open", { source: "map_embed" })}
        className="absolute bottom-4 left-4 inline-flex items-center gap-2 bg-sand text-matcha px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase hover:bg-ink hover:text-sand transition-colors duration-500"
      >
        <MapPin className="w-3.5 h-3.5" />
        {t.location.viewMap}
        <ArrowUpRight className="w-3.5 h-3.5" />
      </a>
    </div>
    <p className="mt-3 text-[11px] text-sand/60 tracking-wide">{t.location.mapNote}</p>
  </FadeUp>
);

/* ------------------------- Sticky mobile CTA bar ----------------------- */

const useScrollFlag = (threshold) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const fn = () => setOn(window.scrollY > threshold);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, [threshold]);
  return on;
};

export const StickyMobileBar = ({ t }) => {
  const show = useScrollFlag(520);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="sticky-mobile-bar"
          initial={{ y: 80 }}
          animate={{ y: 0 }}
          exit={{ y: 80 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-ink/95 backdrop-blur-xl text-sand border-t border-sand/10"
        >
          <div className="px-5 py-3 flex items-center justify-between gap-4">
            <span className="text-[11px] tracking-[0.18em] uppercase text-sand/70 truncate">
              {t.stickyBar.label}
            </span>
            <a
              href={CONTACT.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sticky-bar-whatsapp"
              onClick={() => trackEvent("booking_intent", { channel: "whatsapp", source: "sticky_bar" })}
              className="inline-flex items-center gap-2 bg-whatsapp text-white px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase whitespace-nowrap shrink-0"
            >
              <FaWhatsapp className="w-4 h-4" />
              {t.stickyBar.cta}
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ------------------------------ Back to top ---------------------------- */

export const BackToTop = () => {
  const show = useScrollFlag(900);
  const toTop = () => {
    if (window.lenis) window.lenis.scrollTo(0, { duration: 1.4 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <AnimatePresence>
      {show && (
        <motion.button
          type="button"
          aria-label="Back to top"
          data-testid="back-to-top"
          onClick={toTop}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="fixed z-40 right-4 md:right-6 bottom-24 md:bottom-6 w-12 h-12 border border-ink/30 bg-sand/80 backdrop-blur text-ink flex items-center justify-center hover:bg-ink hover:text-sand transition-colors duration-500"
        >
          <ArrowUp className="w-4 h-4" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};
